import { useDispatch, useSelector } from "react-redux";
import { Action, combineReducers, Dispatch } from "redux";
import { configureStore, ThunkAction } from "@reduxjs/toolkit";
import appDetails from "./reducers/appDetails";
import settings from "./reducers/settings";
import material from "./reducers/material";
import sk from "./reducers/sk";
import tournaments from "./reducers/tournaments";
import authentication from "./reducers/authentication";
import challenges from "./reducers/challenges";
import review from "./reducers/review";

export type ThunkActionType<ReturnType = void> = ThunkAction<
  ReturnType,
  RootStore,
  null,
  Action<string>
>;

/**
 * Initializes redux store. Can be used in tests to have a store unique to that test.
 * @returns a new redux store
 */
export const configureRedux = () => {
  const reducer = combineReducers({
    appDetails,
    authentication,
    challenges,
    material,
    review,
    settings,
    sk,
    tournaments,
  });
  return configureStore({
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: { warnAfter: 128 },
        serializableCheck: { warnAfter: 128 },
      }),
    reducer,
  });
};

const store = configureRedux(); // The redux store

export type RootStore = ReturnType<typeof store.getState>; // The overall type of the redux store

/**
 * Calls useSelector to get data from RootStore
 * @param func the path to the information in redux store that we need
 * @returns A selector
 */
export function useAppSelector<Selected>(func: (state: RootStore) => Selected) {
  return useSelector(func);
}

export const useAppDispatch = (): Dispatch<
  // @ts-ignore dispatch that allows passing a thunk action
  { type: string; payload?: unknown } | ThunkActionType
> => useDispatch();

export default store;
